import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
  alpha
} from '@mui/material'

import { resolveSocrJira } from '../../api/socrInventory'
import { Section } from './Section'

const ClientJiraSection = ({
  token,
  projects = [],
  saving = false,
  onLink,
  onUnlink
}) => {
  const [urlDraft, setUrlDraft] = useState('')
  const [resolving, setResolving] = useState(false)
  const [resolved, setResolved] = useState(null)
  const [resolveError, setResolveError] = useState(null)

  const [jiraKey, setJiraKey] = useState('')
  const [jiraName, setJiraName] = useState('')

  const handleResolve = async () => {
    const url = urlDraft.trim()
    if (!url || !token) return
    setResolving(true)
    setResolveError(null)
    setResolved(null)
    try {
      const row = await resolveSocrJira(token, { url })
      setResolved(row)
    } catch (err) {
      setResolveError(err.message || 'Resolve failed')
    } finally {
      setResolving(false)
    }
  }

  const linkResolved = async () => {
    if (!resolved?.jiraKey) return
    await onLink?.({
      jiraKey: String(resolved.jiraKey).toUpperCase(),
      jiraName: resolved.jiraName || resolved.jiraKey
    })
    setResolved(null)
    setUrlDraft('')
  }

  const linkManual = async () => {
    const key = jiraKey.trim().toUpperCase()
    const name = jiraName.trim() || key
    if (!key) return
    await onLink?.({ jiraKey: key, jiraName: name })
    setJiraKey('')
    setJiraName('')
  }

  return (
    <Section
      title='Jira projects'
      hint='Paste a browse URL or project key to resolve, or add key/name manually.'
    >
      <Stack spacing={1.25}>
        <Stack direction='row' flexWrap='wrap' gap={0.75}>
          {(projects || []).length
            ? projects.map((jira) => (
              <Chip
                key={jira.jiraKey}
                size='small'
                label={`${jira.jiraKey}${jira.jiraName ? ` · ${jira.jiraName}` : ''}`}
                onDelete={saving ? undefined : () => onUnlink?.(jira.jiraKey)}
              />
            ))
            : (
              <Typography variant='caption' sx={{ color: alpha('#fff', 0.5) }}>
                None linked
              </Typography>
              )}
        </Stack>

        {resolveError ? <Alert severity='error'>{resolveError}</Alert> : null}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size='small'
            fullWidth
            label='Jira URL or key'
            placeholder='https://….atlassian.net/browse/FOO-123 or FOO'
            value={urlDraft}
            disabled={resolving || saving}
            onChange={(e) => {
              setUrlDraft(e.target.value)
              setResolved(null)
              setResolveError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleResolve()
              }
            }}
          />
          <Button
            size='small'
            variant='outlined'
            disabled={!urlDraft.trim() || resolving || saving}
            onClick={handleResolve}
          >
            {resolving ? 'Resolving…' : 'Resolve'}
          </Button>
        </Stack>

        {resolved
          ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Chip
                size='small'
                color='info'
                label={`${resolved.jiraKey}${resolved.jiraName ? ` · ${resolved.jiraName}` : ''}`}
              />
              <Button size='small' variant='contained' disabled={saving} onClick={linkResolved}>
                Link to client
              </Button>
            </Stack>
            )
          : null}

        <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

        <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: alpha('#fff', 0.7) }}>
          Manual fallback
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size='small'
            label='Jira key'
            value={jiraKey}
            onChange={(e) => setJiraKey(e.target.value)}
            sx={{ minWidth: { sm: 140 } }}
            disabled={saving}
          />
          <TextField
            size='small'
            label='Display name'
            value={jiraName}
            onChange={(e) => setJiraName(e.target.value)}
            fullWidth
            disabled={saving}
          />
          <Box>
            <Button
              size='small'
              variant='contained'
              disabled={!jiraKey.trim() || saving}
              onClick={linkManual}
            >
              Link
            </Button>
          </Box>
        </Stack>
      </Stack>
    </Section>
  )
}

export { ClientJiraSection }
